import React from 'react';
import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { Row, Col, Divider } from 'antd';
import { NextSeo } from 'next-seo';
import styled from 'styled-components';
import getConfig from 'next/config';
import BasicLayout from 'layout/Basic';
import Content from 'layout/Basic/Content';
import Sider from 'layout/Basic/Sider';
import { convertProps } from 'lib/parseAPIResponse';
import GalleryMainHitbox from 'components/GalleryHitbox/MainHitbox';
import Pagination from 'components/Pagination';
import VideoHitbox from 'components/VideoHitbox';
import Subscription from 'containers/Subscription';
import Section, { SectionTitle } from 'components/Section';
import { ExpandNavibox, HalfPage, Rectangle, SkyscraperMobile, Wideboard } from 'components/Ads';
import Newsletter from 'containers/Newsletter';
import AppSmallLogotypeIcon from 'components/Icons/AppSmallLogotype';
import ArticleCard from 'components/ArticleCard';
import OurIssueSection from 'components/OurIssueSection';
import Item from 'components/List/VideoItem';
import { merriweather } from 'styles/GlobalStyle';
import ContentProvidersSection from 'components/ContentProvidersSection';
import CardGrid from 'components/CardGrid';
import EventsBlock from 'components/EventsBlock';

const H4 = styled.h4`
    color: ${(props) => props.color || props.theme.colors.primary};
    font-family: ${merriweather.style.fontFamily};
    font-size: 18px;
    font-weight: bold;
    font-stretch: normal;
    font-style: normal;
    line-height: 18px;
    letter-spacing: 0.36px;
    text-align: left;
    text-transform: uppercase;
    margin-bottom: 0;
`;

interface Props {
    hitboxVideos: Video[];
    recentVideos: {
        items: Video[];
        results: number;
        currentPage: number;
        numberOfPages: number;
        next: boolean;
        prev: boolean;
        paginate: boolean;
    };
    topVideos: Video[];
    topOneInCampaign: {
        articles: Article[];
        campaign: Campaign[];
    };
    singleWhitePaper: WhitePaper[];
    galleries: Gallery[];
    latestIssue: Issue;
    events: Occurrence[];
    eventsPatronate: Occurrence[];
    companies: Company[];
    ads: PageAds;
}

export const WideoIndexPage = ({
    recentVideos,
    hitboxVideos,
    latestIssue,
    events,
    eventsPatronate,
    topOneInCampaign,
    companies,
    galleries,
    topVideos,
    ads
}: Props): JSX.Element => {
    const router = useRouter();

    const onChange = (page) => {
        router.push(`?page=${page}`);
    };
    const promotedArticle = topOneInCampaign ? topOneInCampaign.articles[0] : null;
    const { name } = getConfig().publicRuntimeConfig;

    const { rectangle, skyscraper, halfpage, navibox, wideboardBottom } = ads;
    return (
        <BasicLayout ads={ads}>
            <NextSeo title={`Wideo | ${name}`} />
            <Row>
                <Col span={24}>
                    <SectionTitle title="Wideo" />
                </Col>
                <Col span={24}>
                    <VideoHitbox videos={hitboxVideos} />
                </Col>
            </Row>
            <Section gutter={16}>
                <Col span={24}>
                    <Divider />
                </Col>
            </Section>
            <Section gutter={16}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <SectionTitle title="Ostatnio dodane wideo" />
                        </Col>
                    </Row>
                    <Row gutter={[16, 16]}>
                        {recentVideos.items.map((item: Video, i: number) => {
                            if (i === 2) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <Item $video={item} />
                                        <Row justify="center">
                                            <Col xs={24} sm={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[0]}
                                                    margin="12px auto 12px auto"
                                                />
                                            </Col>
                                            <Col xs={24} sm={12}>
                                                <SkyscraperMobile
                                                    id={skyscraper[1]}
                                                    margin="12px auto 0 auto"
                                                />
                                            </Col>
                                        </Row>
                                    </Col>
                                );
                            }
                            if (i === 6) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <Item $video={item} />
                                        <ExpandNavibox
                                            id={navibox[0]}
                                            margin="35px auto 15px auto"
                                        />
                                    </Col>
                                );
                            }
                            if (i === 10) {
                                return (
                                    <Col span={24} key={item.seoSlug}>
                                        <Item $video={item} />
                                        <ExpandNavibox
                                            id={navibox[1]}
                                            margin="35px auto 15px auto"
                                        />
                                    </Col>
                                );
                            }

                            return (
                                <Col span={24} key={item.seoSlug}>
                                    <Item $video={item} />
                                </Col>
                            );
                        })}
                    </Row>
                    <Section justify="center">
                        <Col span={24}>
                            <Pagination
                                currentPage={recentVideos.currentPage}
                                results={recentVideos.results}
                                onChange={onChange}
                                next={recentVideos.next}
                                prev={recentVideos.prev}
                            />
                        </Col>
                    </Section>
                </Content>
                <Sider>
                    <Row>
                        <Col xs={{ order: 1, span: 24 }} md={{ order: 1, span: 24 }}>
                            <Rectangle id={rectangle[0]} margin="16px auto" />
                        </Col>
                        <Col xs={{ order: 2, span: 24 }} md={{ order: 2, span: 24 }}>
                            <HalfPage id={halfpage[0]} margin="16px auto" />
                        </Col>
                        {promotedArticle && (
                            <Col xs={{ order: 3, span: 24 }} md={{ order: 3, span: 24 }}>
                                <Section gutter={[16, 16]}>
                                    <Col span={24}>
                                        <H4>
                                            <AppSmallLogotypeIcon style={{ fontSize: 30 }} /> Poleca
                                        </H4>
                                    </Col>
                                    <ArticleCard size={{ span: 24 }} $article={promotedArticle} />
                                </Section>
                            </Col>
                        )}
                        <Col xs={{ order: 4, span: 24 }} md={{ order: 4, span: 24 }}>
                            <Rectangle id={rectangle[1]} margin="16px auto" />
                        </Col>
                        <Col xs={{ order: 5, span: 24 }} md={{ order: 5, span: 24 }}>
                            <HalfPage id={halfpage[1]} margin="16px auto" />
                        </Col>
                        <Col xs={{ order: 6, span: 24 }} md={{ order: 6, span: 24 }}>
                            <Rectangle id={rectangle[2]} margin="16px auto" />
                        </Col>
                        <Col xs={{ order: 7, span: 24 }} md={{ order: 7, span: 24 }}>
                            <Section>
                                <Col span={24}>
                                    <OurIssueSection issue={latestIssue} />
                                </Col>
                            </Section>
                        </Col>
                        <Col xs={{ order: 8, span: 24 }} md={{ order: 8, span: 24 }}>
                            <Rectangle id={rectangle[3]} margin="16px auto" />
                        </Col>
                    </Row>
                </Sider>
            </Section>
            <Row gutter={16}>
                <Content>
                    <Row justify="center">
                        <Col span={24}>
                            <ExpandNavibox id={navibox[2]} margin="35px auto 0 auto" />
                        </Col>
                    </Row>
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <Rectangle id={rectangle[4]} margin="35px auto" />
                        </Col>
                    </Row>
                </Sider>
            </Row>
            <Section gutter={16}>
                <Content>
                    <Row>
                        <Col span={24}>
                            <Divider />
                        </Col>
                        <SectionTitle title="Top Wideo" />
                        <Col span={24}>
                            <CardGrid type="video" items={topVideos} />
                        </Col>
                    </Row>
                    <Section>
                        <Col span={24}>
                            <Section gutter={[16, 32]} justify="center">
                                <Col span={24}>
                                    <Divider />
                                </Col>
                                <Col xs={24} md={20}>
                                    <Newsletter />
                                </Col>
                                <Col span={24}>
                                    <Divider />
                                </Col>
                            </Section>
                        </Col>
                    </Section>
                </Content>
                <Sider>
                    <Section $marginTop={20}>
                        <Col xs={{ order: 8, span: 24 }} md={{ order: 8, span: 24 }}>
                            <HalfPage id={halfpage[2]} />
                        </Col>
                    </Section>
                </Sider>
            </Section>
            <Section>
                <Content>
                    <Row>
                        <SectionTitle title="Galeria" url="/galerie" />
                        <GalleryMainHitbox gallery={galleries[0]} />
                    </Row>

                    <Row>
                        <Col span={24} style={{ paddingTop: 45 }}>
                            <CardGrid
                                items={galleries.slice(1, -1)}
                                type="gallery"
                                url="/galeria"
                                maxItems={4}
                                maxItemsInRowMobile={2}
                            />
                        </Col>
                    </Row>
                </Content>
                <Sider>
                    <Row>
                        <Col span={24}>
                            <HalfPage id={halfpage[3]} margin="16px auto" />
                        </Col>
                        <Col span={24}>
                            <Rectangle id={rectangle[5]} margin="16px auto" />
                        </Col>
                    </Row>
                </Sider>
            </Section>
            <Section gutter={[16, 32]}>
                <Content>
                    <ContentProvidersSection companies={companies} />
                </Content>
                <Sider>
                    <Divider />
                    <Col span={24}>
                        <EventsBlock events={events} eventsPatronate={eventsPatronate} />
                    </Col>
                </Sider>
            </Section>
            <Section gutter={[16, 32]}>
                <Col span={24}>
                    <Subscription issue={latestIssue} />
                </Col>
                <Col span={24}>
                    <Wideboard id={wideboardBottom[0]} />
                </Col>
            </Section>
        </BasicLayout>
    );
};

export const getServerSideProps: GetServerSideProps = async ({ query }) => {
    const page = typeof query.page === 'string' ? parseInt(query.page) : 1;

    const { apiUrl } = getConfig().publicRuntimeConfig;
    return Promise.all([
        fetch(`${apiUrl}/public/api/videos,${page}`),
        fetch(`${apiUrl}/public/api/naszadsik?path=wideo`)
    ]).then(async ([res1, res2]) => {
        const data = await res1.json();
        if (data.code === 404) {
            return {
                notFound: true
            };
        }
        const ads = await res2.json();

        return { props: { ...convertProps(ads), ...convertProps(data) } };
    });
};

export default WideoIndexPage;
